import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface TrendPoint {
  period: string;
  count: number;
  label: string;
}

interface TrendData {
  monthly_uploads: TrendPoint[];
  monthly_approvals: TrendPoint[];
}

interface TrendChartProps {
  data: TrendData;
  height?: number;
}

// Merge upload and approval arrays into a single series by period
function mergeData(data: TrendData) {
  const map = new Map<string, { label: string; uploads: number; approvals: number }>();

  for (const pt of data.monthly_uploads) {
    map.set(pt.period, { label: pt.label, uploads: pt.count, approvals: 0 });
  }
  for (const pt of data.monthly_approvals) {
    const existing = map.get(pt.period);
    if (existing) {
      existing.approvals = pt.count;
    } else {
      map.set(pt.period, { label: pt.label, uploads: 0, approvals: pt.count });
    }
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, values]) => ({
      period,
      ...values,
    }));
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function TrendChart({ data, height = 280 }: TrendChartProps) {
  const chartData = mergeData(data);

  if (chartData.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        Tidak ada data untuk ditampilkan
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12, fill: "#6b7280" }}
          tickLine={false}
          axisLine={{ stroke: "#e5e7eb" }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: "#6b7280" }}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px", paddingTop: "16px" }}
          formatter={(value) =>
            value === "uploads" ? "Dokumen Diunggah" : "Dokumen Disetujui"
          }
        />
        <Line
          type="monotone"
          dataKey="uploads"
          name="uploads"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={{ r: 3, fill: "#3b82f6" }}
          activeDot={{ r: 5 }}
        />
        <Line
          type="monotone"
          dataKey="approvals"
          name="approvals"
          stroke="#10b981"
          strokeWidth={2}
          dot={{ r: 3, fill: "#10b981" }}
          activeDot={{ r: 5 }}
          strokeDasharray="5 5"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
